import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSendMessage } from "@/features/tasks/hooks";
import { useSendGeneralMessage } from "@/features/messages/hooks";
import { MessageProvider } from "@/contexts/MessageContext";
import { useQueryClient } from "@tanstack/react-query";
import { tasksApi } from "@/features/tasks/api";

interface MessageContextWrapperProps {
  children: React.ReactNode;
  shouldProvideContext: boolean;
}

export function MessageContextWrapper({ children, shouldProvideContext }: MessageContextWrapperProps) {
  const { taskId } = useParams<{ taskId: string }>();
  const [searchParams] = useSearchParams();
  const taskIdNumber = parseInt(taskId || "0", 10);
  const instanceIdParam = searchParams.get("instance");
  const instanceId = instanceIdParam ? parseInt(instanceIdParam, 10) : undefined;
  const location = useLocation();
  const isMessagesPage = location.pathname === "/messages";
  
  // Only override context if we're on a page that needs it
  if (!shouldProvideContext) {
    return <>{children}</>;
  }

  const queryClient = useQueryClient();

  // Handle task details pages
  if (taskIdNumber > 0) {
    const [isPending, setIsPending] = useState(false);

    const handleSendMessage = async (message: { body: string; photoUrl?: string }) => {
      console.log("[MessageContextWrapper] handleSendMessage called", {
        taskIdNumber,
        instanceId,
        instanceIdParam,
        message,
        url: location.pathname + location.search,
      });
      
      setIsPending(true);
      try {
        await tasksApi.sendMessage(taskIdNumber, message, instanceId);
        // 🔁 refresh the panel's data
        queryClient.invalidateQueries({ queryKey: ["tasks", taskIdNumber, "messages"] });
      } catch (error) {
        console.error("Failed to send message:", error);
        throw error;
      } finally {
        setIsPending(false);
      }
    };

    return (
      <MessageProvider 
        onSendMessage={handleSendMessage} 
        isPending={isPending}
      >
        {children}
      </MessageProvider>
    );
  }

  // Handle messages page
  if (isMessagesPage) {
    const sendGeneralMessageMutation = useSendGeneralMessage();

    const handleSendMessage = async (message: { body: string; photoUrl?: string }) => {
      await sendGeneralMessageMutation.mutateAsync(message);
    };

    return (
      <MessageProvider 
        onSendMessage={handleSendMessage} 
        isPending={sendGeneralMessageMutation.isPending}
      >
        {children}
      </MessageProvider>
    );
  }

  // Fallback - shouldn't reach here
  return <>{children}</>;
}
